import type { TProduct } from "@lexicanon/shared"
import {
  type CSSVariablesResolver,
  createTheme,
  defaultVariantColorsResolver,
  type MantineColorsTuple,
  type MantineThemeOverride,
  type VariantColorsResolver,
} from "@mantine/core"

/**
 * Semantic palette names — used by code in place of literal Mantine colours
 * so a Badge / Notification / CSS-module reference says WHY (success,
 * danger, warning) instead of HOW (teal, red, yellow). Each product owns
 * its own definition.
 *
 * Rule: use a semantic name when the colour expresses a *meaning* (success
 * → "this worked", danger → "this failed", brand → "this is product
 * chrome"). Use a literal palette index only when the colour expresses an
 * *identity* (NPC #2 vs NPC #3, allied faction vs hostile faction —
 * functionally distinct chromas, not bundled meanings).
 *
 * Available semantics:
 *   brand     — product chrome (CTAs, primary highlights)
 *   success   — completed, confirmed, accepted, "ok"
 *   danger    — error, destructive, "this failed"
 *   warning   — pending, attention-required, illuminated state
 *   info      — neutral-positive, "FYI", non-actionable detail
 *   highlight — rare emphasis, special / exotic accent
 */

// Mantine v8 requires a 10-tuple for theme.colors.X.
const tuple = (
  c0: string,
  c1: string,
  c2: string,
  c3: string,
  c4: string,
  c5: string,
  c6: string,
  c7: string,
  c8: string,
  c9: string,
): MantineColorsTuple => [c0, c1, c2, c3, c4, c5, c6, c7, c8, c9]

// ---------------------------------------------------------------------------
// Lexicanon palettes — static hexes, no theme-time changes.
// ---------------------------------------------------------------------------

const LEX_TEAL = tuple(
  "#e0faf8",
  "#b2f0eb",
  "#81e6df",
  "#4fd1ca",
  "#38b2ac",
  "#2c9e98",
  "#1d8a84",
  "#0d9488",
  "#0a7a70",
  "#065f5a",
)

const LEX_RED = tuple(
  "#fff5f5",
  "#ffe3e3",
  "#ffc9c9",
  "#ffa8a8",
  "#ff8787",
  "#fa5252",
  "#f03e3e",
  "#e03131",
  "#c92a2a",
  "#a51a1a",
)

const LEX_AMBER = tuple(
  "#fff9db",
  "#fff3bf",
  "#ffec99",
  "#ffe066",
  "#ffd43b",
  "#fcc419",
  "#fab005",
  "#f59f00",
  "#f08c00",
  "#e67700",
)

const LEX_BLUE = tuple(
  "#e7f5ff",
  "#d0ebff",
  "#a5d8ff",
  "#74c0fc",
  "#4dabf7",
  "#339af0",
  "#228be6",
  "#1c7ed6",
  "#1971c2",
  "#1864ab",
)

const LEX_INDIGO = tuple(
  "#edf2ff",
  "#dbe4ff",
  "#bac8ff",
  "#91a7ff",
  "#748ffc",
  "#5c7cfa",
  "#4c6ef5",
  "#4263eb",
  "#3b5bdb",
  "#364fc7",
)

const LEX_GRAY = tuple(
  "#f8f9fa",
  "#f1f3f5",
  "#e9ecef",
  "#dee2e6",
  "#ced4da",
  "#adb5bd",
  "#868e96",
  "#495057",
  "#343a40",
  "#212529",
)

// ---------------------------------------------------------------------------
// Voxcanon palettes — placeholder hexes (sci-fi tones). The voxcanonCss-
// Variables resolver overrides every --mantine-color-{name}-N to point at
// the matching --vox-* token, so the palettes auto-theme between fantasy
// and sci-fi without changes here. These literals only ever render if
// the resolver is bypassed (e.g. SSR fallback that we don't have today).
// ---------------------------------------------------------------------------

const VOX_ARCANE = tuple(
  "#f3eeff",
  "#e4d5fd",
  "#ccaffc",
  "#a78bfa",
  "#8b5cf6",
  "#7c3aed",
  "#6d28d9",
  "#5b21b6",
  "#3b0764",
  "#1e0038",
)

const VOX_ETHER = tuple(
  "#e0faf8",
  "#b2f0eb",
  "#81e6df",
  "#4fd1ca",
  "#06b6d4",
  "#0891b2",
  "#0e7490",
  "#155e75",
  "#164e63",
  "#0a4a5a",
)

const VOX_CRIMSON = tuple(
  "#fff5f5",
  "#fed7d7",
  "#feb2b2",
  "#fc8181",
  "#f56565",
  "#dc2626",
  "#c53030",
  "#b91c1c",
  "#991b1b",
  "#7f1d1d",
)

const VOX_GOLD = tuple(
  "#fffdf0",
  "#fef9d0",
  "#fef08a",
  "#fbbf24",
  "#d4a843",
  "#d97706",
  "#b45309",
  "#92400e",
  "#78350f",
  "#3d1a04",
)

const VOX_HIGHLIGHT = tuple(
  "#fff5f5",
  "#fed7d7",
  "#fbb6ce",
  "#f687b3",
  "#ed64a6",
  "#d53f8c",
  "#b83280",
  "#97266d",
  "#702459",
  "#521b41",
)

// ---------------------------------------------------------------------------
// Lexicanon CSS-variables resolver
// ---------------------------------------------------------------------------

/**
 * Passed to <MantineProvider cssVariablesResolver=…> for Lexicanon. Maps the
 * six semantic Mantine palettes to the `--lex-*` tokens defined in
 * styles/lexicanon-tokens.css. The brand ramp specifically resolves to
 * `--lex-brand-*` (Layer 1.5), which is the single seam Phase 2's white-label
 * code overrides per company — touching no other palette.
 *
 * The visible result must be byte-identical to the static LEX_* tuples above;
 * this resolver is plumbing, not a design change. Tuples remain wired into
 * `theme.colors` as the no-resolver fallback.
 */
export const lexicanonCssVariables: CSSVariablesResolver = () => {
  const slot = (mantine: string, lex: string): Record<string, string> =>
    Object.fromEntries(
      Array.from({ length: 10 }, (_, i) => [`--mantine-color-${mantine}-${i}`, `var(--lex-${lex}-${i})`]),
    )
  const both = {
    "--mantine-primary-color-filled": "var(--lex-brand-7)",
    "--mantine-primary-color-filled-hover": "var(--lex-brand-8)",
    ...slot("brand", "brand"),
    ...slot("success", "teal"),
    ...slot("danger", "red"),
    ...slot("warning", "amber"),
    ...slot("info", "blue"),
    ...slot("highlight", "indigo"),
  }
  // Soften the default text colours so headings can sit *above* body text on
  // weight + colour, not just weight. Mantine's default makes both
  // `--mantine-color-bright` (Title) and `--mantine-color-text` (body)
  // resolve to pure black in light mode → no visible hierarchy.
  const light = {
    ...both,
    "--mantine-color-bright": "var(--lex-gray-9)", // #212529 — near-black for Titles
    "--mantine-color-text": "var(--lex-gray-8)", //   #343a40 — softer for body
    "--mantine-color-dimmed": "var(--lex-gray-6)", // #868e96 — dimmed metadata
  }
  // Dark mode already gets a softened text from Mantine (dark-0 ≈ #c9c9c9);
  // leave it alone but pin bright to a near-white so Titles read clearly.
  const dark = {
    ...both,
    "--mantine-color-bright": "var(--lex-gray-0)", // #f8f9fa
  }
  return { variables: {}, light, dark }
}

// ---------------------------------------------------------------------------
// Voxcanon CSS-variables resolver
// ---------------------------------------------------------------------------

/**
 * Passed to <MantineProvider cssVariablesResolver=…> for Voxcanon only.
 * Every value points at a --vox-* semantic token defined in
 * styles/voxcanon-tokens.css; those tokens re-map per `data-theme` so a
 * single resolver covers both fantasy and sci-fi surfaces. We keep
 * Mantine on a single fixed scheme (dark) — the visual swap happens via
 * the data-theme attribute on <html>, not Mantine's color-scheme.
 *
 * Public surface is the six semantic palettes (`--mantine-color-brand-*`,
 * `success-*`, `danger-*`, `warning-*`, `info-*`, `highlight-*`). Code
 * should reference those by their semantic role; literal Mantine palette
 * names like `color="teal"` are deliberately not aliased — names should
 * tell the truth about what they mean.
 */
export const voxcanonCssVariables: CSSVariablesResolver = () => ({
  variables: {},
  light: {},
  dark: {
    "--mantine-color-body": "var(--vox-void)",
    "--mantine-color-default": "var(--vox-obsidian)",
    "--mantine-color-dark-9": "var(--vox-void)",
    "--mantine-color-dark-8": "var(--vox-abyss)",
    "--mantine-color-dark-7": "var(--vox-obsidian)",
    "--mantine-color-dark-6": "var(--vox-shadow)",
    "--mantine-color-dark-5": "var(--vox-dusk)",
    "--mantine-color-dark-4": "var(--vox-arcane-deep)",
    "--mantine-color-dark-3": "var(--vox-arcane)",
    "--mantine-color-dark-2": "var(--vox-text-muted)",
    "--mantine-color-dark-1": "var(--vox-text-dim)",
    "--mantine-color-dark-0": "var(--vox-text)",
    "--mantine-color-default-border": "var(--vox-border-soft)",
    "--mantine-color-text": "var(--vox-text)",
    "--mantine-color-dimmed": "var(--vox-text-dim)",
    "--mantine-color-placeholder": "var(--vox-text-muted)",
    "--mantine-color-anchor": "var(--vox-arcane-light)",

    // ─── Semantic palettes (PUBLIC) ─────────────────────────────────────
    // These are the names code should use. They auto-theme via --vox-*.
    //
    // brand — product chrome (arcane violet on each surface)
    "--mantine-color-brand-0": "color-mix(in oklab, var(--vox-arcane) 12%, var(--vox-obsidian))",
    "--mantine-color-brand-1": "color-mix(in oklab, var(--vox-arcane) 22%, var(--vox-obsidian))",
    "--mantine-color-brand-2": "var(--vox-arcane-bright)",
    "--mantine-color-brand-3": "var(--vox-arcane-light)",
    "--mantine-color-brand-4": "var(--vox-arcane)",
    "--mantine-color-brand-5": "var(--vox-arcane)",
    "--mantine-color-brand-6": "var(--vox-arcane-deep)",
    "--mantine-color-brand-7": "var(--vox-arcane-deep)",
    "--mantine-color-brand-8": "var(--vox-arcane-deep)",
    "--mantine-color-brand-9": "var(--vox-arcane-deep)",

    // success — confirmed / completed / accepted (ether: forest/cyan)
    "--mantine-color-success-0": "color-mix(in oklab, var(--vox-ether) 12%, var(--vox-obsidian))",
    "--mantine-color-success-1": "color-mix(in oklab, var(--vox-ether) 22%, var(--vox-obsidian))",
    "--mantine-color-success-2": "color-mix(in oklab, var(--vox-ether) 35%, var(--vox-obsidian))",
    "--mantine-color-success-3": "var(--vox-ether)",
    "--mantine-color-success-4": "var(--vox-ether)",
    "--mantine-color-success-5": "var(--vox-ether)",
    "--mantine-color-success-6": "var(--vox-ether-deep)",
    "--mantine-color-success-7": "var(--vox-ether-deep)",
    "--mantine-color-success-8": "var(--vox-ether-deep)",
    "--mantine-color-success-9": "var(--vox-ether-deep)",

    // danger — error / destructive / "this failed" (crimson / wax-seal)
    "--mantine-color-danger-0": "color-mix(in oklab, var(--vox-crimson) 12%, var(--vox-obsidian))",
    "--mantine-color-danger-1": "color-mix(in oklab, var(--vox-crimson) 22%, var(--vox-obsidian))",
    "--mantine-color-danger-2": "color-mix(in oklab, var(--vox-crimson) 35%, var(--vox-obsidian))",
    "--mantine-color-danger-3": "var(--vox-crimson-light)",
    "--mantine-color-danger-4": "var(--vox-crimson)",
    "--mantine-color-danger-5": "var(--vox-crimson)",
    "--mantine-color-danger-6": "var(--vox-crimson)",
    "--mantine-color-danger-7": "var(--vox-crimson)",
    "--mantine-color-danger-8": "var(--vox-crimson)",
    "--mantine-color-danger-9": "var(--vox-crimson)",

    // warning — pending / attention / illuminated (gold / sienna)
    "--mantine-color-warning-0": "color-mix(in oklab, var(--vox-gold) 12%, var(--vox-obsidian))",
    "--mantine-color-warning-1": "color-mix(in oklab, var(--vox-gold) 22%, var(--vox-obsidian))",
    "--mantine-color-warning-2": "color-mix(in oklab, var(--vox-gold) 35%, var(--vox-obsidian))",
    "--mantine-color-warning-3": "var(--vox-gold-light)",
    "--mantine-color-warning-4": "var(--vox-gold)",
    "--mantine-color-warning-5": "var(--vox-gold)",
    "--mantine-color-warning-6": "var(--vox-gold-deep)",
    "--mantine-color-warning-7": "var(--vox-gold-deep)",
    "--mantine-color-warning-8": "var(--vox-gold-deep)",
    "--mantine-color-warning-9": "var(--vox-gold-deep)",

    // info — neutral-positive / FYI (a softer arcane-light tone)
    "--mantine-color-info-0": "color-mix(in oklab, var(--vox-arcane-light) 12%, var(--vox-obsidian))",
    "--mantine-color-info-1": "color-mix(in oklab, var(--vox-arcane-light) 22%, var(--vox-obsidian))",
    "--mantine-color-info-2": "var(--vox-arcane-bright)",
    "--mantine-color-info-3": "var(--vox-arcane-bright)",
    "--mantine-color-info-4": "var(--vox-arcane-light)",
    "--mantine-color-info-5": "var(--vox-arcane-light)",
    "--mantine-color-info-6": "var(--vox-arcane)",
    "--mantine-color-info-7": "var(--vox-arcane-deep)",
    "--mantine-color-info-8": "var(--vox-arcane-deep)",
    "--mantine-color-info-9": "var(--vox-arcane-deep)",

    // highlight — rare emphasis / special accent (rose / wine)
    "--mantine-color-highlight-0": "color-mix(in oklab, var(--vox-crimson-light) 12%, var(--vox-obsidian))",
    "--mantine-color-highlight-1": "color-mix(in oklab, var(--vox-crimson-light) 22%, var(--vox-obsidian))",
    "--mantine-color-highlight-2": "var(--vox-crimson-light)",
    "--mantine-color-highlight-3": "var(--vox-crimson-light)",
    "--mantine-color-highlight-4": "var(--vox-crimson-light)",
    "--mantine-color-highlight-5": "var(--vox-crimson)",
    "--mantine-color-highlight-6": "var(--vox-crimson)",
    "--mantine-color-highlight-7": "var(--vox-crimson)",
    "--mantine-color-highlight-8": "var(--vox-crimson)",
    "--mantine-color-highlight-9": "var(--vox-crimson)",
  },
})

// ---------------------------------------------------------------------------
// Themes
// ---------------------------------------------------------------------------

/**
 * Width at which the full horizontal navigation fits in the header. Below
 * this the app falls back to the burger menu + nav drawer. Set to 1080px so
 * every iPad in portrait (≤1024px, incl. the 12.9″ Pro) uses the burger,
 * while iPads in landscape (≥1080px) keep the full nav. Merges with — does
 * not replace — Mantine's default xs–xl breakpoints. Consumed via the
 * `navFull` key in `visibleFrom`/`hiddenFrom` (see routes/_auth.tsx).
 */
const NAV_FULL_BREAKPOINT = "67.5em" // 1080px

/**
 * Lexicanon-only resolver tweak: brand/primary *filled* surfaces read their
 * label colour from `--mantine-color-brand-contrast` (falling back to white).
 *
 * Why: most Lexicanon dark themes invert the brand ramp, so the filled shade
 * (`brand-7`) becomes a light/bright tint (pastel → `#f08cae`, signal → bright
 * teal, …). Mantine keeps filled-button text white, which then sits illegibly
 * on the pale fill. The dark blocks that invert set `--mantine-color-brand-
 * contrast` to a dark ink in `lexicanon-tokens.css`; light mode leaves it unset
 * → white, so the (good) light surfaces stay byte-identical. Non-brand colours
 * (`danger`, `success`, …) keep Mantine's default white-on-filled, since their
 * fills stay dark in dark mode.
 */
const lexVariantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input)
  const isBrand = input.color === undefined || input.color === "brand"
  if (input.variant === "filled" && isBrand) {
    return { ...resolved, color: "var(--mantine-color-brand-contrast, var(--mantine-color-white))" }
  }
  return resolved
}

export const lexicanonTheme = createTheme({
  primaryColor: "brand",
  variantColorResolver: lexVariantColorResolver,
  // Filled surfaces with a light fill (warning amber, avatar tints, …) get a
  // dark ink label instead of Mantine's default white, which measured as low
  // as 1.86:1 (white on amber) in the WCAG sweep.
  autoContrast: true,
  luminanceThreshold: 0.3,
  fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  fontFamilyMonospace: "'JetBrains Mono', monospace",
  defaultRadius: "md",
  cursorType: "pointer",
  breakpoints: { navFull: NAV_FULL_BREAKPOINT },
  colors: {
    // Lexicanon brand IS teal, so brand and success share the ramp today.
    // They're separate keys so they can diverge later without coupling.
    brand: LEX_TEAL,
    success: LEX_TEAL,
    danger: LEX_RED,
    warning: LEX_AMBER,
    info: LEX_BLUE,
    highlight: LEX_INDIGO,
    gray: LEX_GRAY,
  },
})

export const voxcanonTheme = createTheme({
  primaryColor: "brand",
  autoContrast: true,
  luminanceThreshold: 0.3,
  fontFamily: "'Nunito', 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  fontFamilyMonospace: "'JetBrains Mono', monospace",
  headings: {
    fontFamily: "'Cinzel', 'Georgia', serif",
    fontWeight: "700",
    sizes: {
      h1: { fontSize: "2.2rem", lineHeight: "1.2" },
      h2: { fontSize: "1.6rem", lineHeight: "1.3" },
      h3: { fontSize: "1.25rem", lineHeight: "1.4" },
    },
  },
  defaultRadius: "md",
  cursorType: "pointer",
  breakpoints: { navFull: NAV_FULL_BREAKPOINT },

  // The literals here only render if the cssVariablesResolver is bypassed
  // (we don't currently bypass it). Real values are injected by
  // voxcanonCssVariables and auto-theme via --vox-* tokens.
  colors: {
    brand: VOX_ARCANE,
    success: VOX_ETHER,
    danger: VOX_CRIMSON,
    warning: VOX_GOLD,
    info: VOX_ARCANE,
    highlight: VOX_HIGHLIGHT,
  },

  shadows: {
    sm: "0 1px 3px rgba(0,0,0,0.4), 0 1px 8px rgba(139,92,246,0.08)",
    md: "0 2px 12px rgba(0,0,0,0.5), 0 0 20px rgba(139,92,246,0.12)",
    lg: "0 4px 24px rgba(0,0,0,0.6), 0 0 40px rgba(139,92,246,0.18)",
    xl: "0 8px 40px rgba(0,0,0,0.7), 0 0 60px rgba(139,92,246,0.25)",
  },

  components: {
    Paper: {},
    Card: {},
    Button: {
      defaultProps: {
        variant: "filled",
      },
    },
    Modal: {
      defaultProps: {
        overlayProps: { blur: 4 },
      },
    },
    Badge: {
      defaultProps: {
        radius: "sm",
      },
    },
  },
})

const themes: Record<TProduct, MantineThemeOverride> = {
  lexicanon: lexicanonTheme,
  voxcanon: voxcanonTheme,
}

export function getTheme(product: TProduct): MantineThemeOverride {
  return themes[product]
}
